import { useMemo } from 'react';
import {
	Card,
	Dropdown,
	makeStyles,
	mergeClasses,
	Option,
	tokens
} from '@fluentui/react-components';

import {Dismiss16Filled} from '@fluentui/react-icons';
import { sortByDate } from '../utils/sort-by-date';

import { conditionOptions, conditionTypes, useAutomationRuleConditions } from './use-conditions';

import FieldMeetCertainCriteria from './field-meet-certain-criteria';
import FormDividerConditions from './divider';
import ChecklistCompletedOrNotCompletedCondition from './checklist-completed-or-not-completed';
import { useChecklistCompletedOrNotCompletedCondition } from './checklist-completed-or-not-completed/use-checklist-completed-or-not-completed-condition';
import { useFieldMeetCertainCriteria } from './field-meet-certain-criteria/use-field-meet-certain-criteria';
import FieldMeetCertainCriteriaRelationshipInput from './field-meet-certain-criteria/relationship-input';
import FieldMeetCertainCriteriaRelationshipCheckInput from './field-meet-certain-criteria/relationship-check-input';
import { ConditionItemType } from './types';

interface ConditionItemProps {
	id: string;
	condition: string;
}

const ConditionItem = (props: ConditionItemProps) => {
	const classes = useStyles();
	const {
		conditionValidationErrors,
		setConditions,
		resetValidationErrors,
		removeConditionItem,
		conditions
	} = useAutomationRuleConditions();

	const checklistCompletedOrNotCompletedConditionHook = useChecklistCompletedOrNotCompletedCondition();
	const fieldMeetCertainCriteriaHook = useFieldMeetCertainCriteria();

	const currentValidation = useMemo(() => {
		const found = conditionValidationErrors.find(item => item.conditionId === props.id);
		return found;
	}, [props.id, conditionValidationErrors]);

	const hasRelationshipCheck = useMemo(() => {
		let hasRelationshipCheckResult = false;

		if (props.condition === conditionTypes.fieldMeetACertainCriteria) {
			const foundCondition = conditions.find(item => item.id === props.id) as ConditionItemType;
			if (foundCondition && (
				foundCondition.fieldMeetCertainCriteria?.relationship === 'sibling-work-items' ||
				foundCondition.fieldMeetCertainCriteria?.relationship === 'children-work-items')
			) {
				hasRelationshipCheckResult = true;
			}
		}

		return hasRelationshipCheckResult;
	}, [props.condition, conditions]);

	const renderSubForm = useMemo(() => {
		if (props.condition === conditionTypes.fieldMeetACertainCriteria) {
			return (
				<FieldMeetCertainCriteria
					conditionId={props.id}
					validations={currentValidation?.fieldMeetACertainCriteria || null}
				/>
			);
		}

		if (props.condition === conditionTypes.checklistIsCompleted || props.condition === conditionTypes.checklistIsNotCompleted) {
			return (
				<ChecklistCompletedOrNotCompletedCondition
					conditionId={props.id}
					validations={currentValidation?.checklistCompletedOrNotCompletedCondition || null}
				/>
			);
		}

		return null;
	}, [
		props.id,
		props.condition,
		currentValidation
	]);

	return (
		<>
			<div className={classes.deleteButtonRoot}>
				<button
					className={classes.deleteBtn}
					onClick={(ev) => {
						ev.preventDefault();
						removeConditionItem(props.id);
					}}
				>
					<Dismiss16Filled />
				</button>
			</div>
			<div style={{ width: '100%' }}>
				<Dropdown
					className={mergeClasses(
						classes.dropdown,
						classes.conditionTypeDropdown,
						currentValidation?.condition ? classes.dropdownWithError : ''
					)}
					listbox={{
						style: {
							minWidth: 'fit-content'
						}
					}}
					appearance='underline'
					placeholder='And'
					positioning="below-start"
					value={conditionOptions.find((item => item.value === props.condition))?.label || ''}
					onOptionSelect={async (_, data) => {
						resetValidationErrors();

						if (data.optionValue === conditionTypes.checklistIsCompleted || data.optionValue === conditionTypes.checklistIsNotCompleted) {
							checklistCompletedOrNotCompletedConditionHook.setCondition({
								setConditions,
								conditionId: props.id,
								condition: data.optionValue,
							});
						}

						if (data.optionValue === conditionTypes.fieldMeetACertainCriteria) {
							fieldMeetCertainCriteriaHook.setCondition({
								setConditions,
								conditionId: props.id,
								condition: data.optionValue,
							});
						}
					}}
				>
					{conditionOptions.map((condition) => (
						<Option key={condition.value} value={condition.value}>
							{condition.label}
						</Option>
					))}
				</Dropdown>

				{
					props.condition === conditionTypes.fieldMeetACertainCriteria && (
						<FieldMeetCertainCriteriaRelationshipInput
							conditionId={props.id}
							validationError={null}
						/>
					)
				}
				{
					props.condition === conditionTypes.fieldMeetACertainCriteria && hasRelationshipCheck && (
						<FieldMeetCertainCriteriaRelationshipCheckInput
							conditionId={props.id}
						/>
					)
				}
			</div>
			{
				currentValidation?.condition && (
					<span className={classes.helperTextForError}>
						{currentValidation?.condition}
					</span>
				)
			}
			{renderSubForm}
		</>
	);
};

export default function AutomationRuleConditions() {
	const {conditions} = useAutomationRuleConditions();

	return (
		<>
			{conditions
				.sort((a, b) => sortByDate(a.created_at, b.created_at))
				.map((item, idx) => (
					<div key={item.id} style={{width: '100%'}}>
						<Card key={item.id}>
							<ConditionItem
								id={item.id}
								condition={item.condition as string}
							/>
						</Card>
						<FormDividerConditions
							isLast={idx === conditions.length - 1}
						/>
					</div>
				))}
		</>
	);
}

const useStyles = makeStyles({
	root: {},
	row: {},
	dropdown: {
		minWidth: 'auto',
		borderBottom: 'none',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		'::after': {
			borderBottom: 'none'
		},
		':hover': {
			background: tokens.colorNeutralShadowAmbientLighter
		},
	},
	helperTextForError: {
		width: '100%',
		color: tokens.colorPaletteRedBorder2,
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase100,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase100,
		padding: '0px 4px'
	},
	dropdownWithError: {
		backgroundColor: tokens.colorPaletteRedBackground2,
		'& button': {
			color: tokens.colorPaletteRedBorder2,
		},
		'& svg': {
			color: tokens.colorPaletteRedBorder2,
		},
		':hover': {
			backgroundColor: tokens.colorPaletteRedBackground2,
		},
		':active': {
			background: tokens.colorNeutralBackground4Hover,
		}
	},
	conditionTypeDropdown: {
		maxWidth: '240px'
	},
	subForm: {
		width: '100%',
		display: 'flex',
	},
	deleteButtonRoot: {
		position: 'absolute',
		zIndex: 1,
		right: 0,
		top: 0,
		padding: '8px'
	},
	deleteBtn: {
		padding: '2px',
		border: 'none',
		backgroundColor: 'transparent',
		cursor: 'pointer',
		color: tokens.colorNeutralStrokeAccessible
	},
});